import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../common-components/Modal";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";
import TertiaryButton from "../../common-components/Buttons/TertiaryButton";
import useCreateDoctorStatic from "./hooks/useCreateDoctorStatic";
import useUpdateDoctorStatic from "./hooks/useUpdateDoctorStatic";
import usePatchDoctorStatic from "./hooks/usePatchDoctorStatic";
import { useTranslation } from 'react-i18next';

const CreateDoctorStaticModal = ({
  isOpen = false,
  setIsOpen = () => {},
  getAllDoctorStatic = () => {},
  isEditable = false,
  currentDoctorStatic = {},
  setCurrentDoctorStatic = () => {},
  setIsEditable = () => {},
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    doctor_name: "",
    photo_url: "",
    designation_name: "",
    qualifications: "",
    experience_years: "",
    languages: "",
    photo_file: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  const { onCreateDoctorStatic, isDoctorStaticCreating } = useCreateDoctorStatic({
    getAllDoctorStatic,
    onCloseModal: () => {
      setIsOpen(false);
      setIsEditable(false);
      setCurrentDoctorStatic({});
    },
  });

  const { onUpdateDoctorStatic, isDoctorStaticUpdating } = useUpdateDoctorStatic({
    getAllDoctorStatic,
    onCloseModal: () => {
      setIsOpen(false);
      setIsEditable(false);
      setCurrentDoctorStatic({});
    },
  });

  const { onPatchDoctorStatic, isDoctorStaticPatching } = usePatchDoctorStatic({
    getAllDoctorStatic,
    onCloseModal: () => {
      setIsOpen(false);
      setIsEditable(false);
      setCurrentDoctorStatic({});
    },
  });

  useEffect(() => {
    if (isEditable && currentDoctorStatic && Object.keys(currentDoctorStatic).length > 0) {
      // Edit mode: use all data from currentDoctorStatic
      setForm({
        doctor_name: currentDoctorStatic.doctor_name || "",
        photo_url: currentDoctorStatic.photo_url || "",
        designation_name: currentDoctorStatic.designation_name || "",
        qualifications: currentDoctorStatic.qualifications || "",
        experience_years: currentDoctorStatic.experience_years || "",
        languages: currentDoctorStatic.languages || "",
        photo_file: null,
      });
      if (currentDoctorStatic.photo_url) {
        setPhotoPreview(currentDoctorStatic.photo_url);
      }
    } else if (!isEditable && currentDoctorStatic && Object.keys(currentDoctorStatic).length > 0) {
      // Add mode: pre-fill doctor_name and photo_url if available
      setForm({
        doctor_name: currentDoctorStatic.doctor_name || "",
        photo_url: currentDoctorStatic.photo_url || "",
        designation_name: "",
        qualifications: "",
        experience_years: "",
        languages: "",
        photo_file: null,
      });
      if (currentDoctorStatic.photo_url) {
        setPhotoPreview(currentDoctorStatic.photo_url);
      } else {
        setPhotoPreview(null);
      }
    } else {
      // Empty form
      setForm({
        doctor_name: "",
        photo_url: "",
        designation_name: "",
        qualifications: "",
        experience_years: "",
        languages: "",
        photo_file: null,
      });
      setPhotoPreview(null);
    }
  }, [isEditable, currentDoctorStatic, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file' && files && files.length > 0) {
      const file = files[0];
      setForm((prev) => ({
        ...prev,
        [name]: file,
      }));
      // Create preview for image
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const docId = currentDoctorStatic?.docId;
    
    if (docId === undefined || docId === null) {
      toast.error("Doctor ID is required");
      return;
    }
    
    // Add mode (isEditable = false): Use PUT with FormData
    // Edit mode (isEditable = true): Use PATCH with JSON
    if (!isEditable) {
      // Add mode: Always use PUT with FormData (PUT endpoint requires multipart/form-data)
      const formData = new FormData();
      
      // List of fields that should be sent to the API
      const fieldsToSend = ['doctor_name', 'designation_name', 'qualifications', 'experience_years', 'languages', 'photo_url'];
      
      // Add all form fields (only include fields that have values)
      fieldsToSend.forEach((key) => {
        if (form[key] !== undefined && form[key] !== null && form[key] !== "") {
          // Convert experience_years to integer if it's a number
          if (key === 'experience_years') {
            const value = parseInt(form[key], 10);
            if (!isNaN(value)) {
              formData.append(key, value);
            } else {
              formData.append(key, form[key]);
            }
          } else {
            formData.append(key, form[key]);
          }
        }
      });
      
      // Add photo file if provided
      if (form.photo_file) {
        formData.append('photo_file', form.photo_file);
      }
      
      // Log FormData contents for debugging
      console.log('PUT - FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? `[File: ${pair[1].name}]` : pair[1]));
      }
      
      await onUpdateDoctorStatic(docId, formData, true);
    } else {
      // Edit mode: Always use PATCH with FormData (external API requires multipart/form-data)
      const formData = new FormData();
      
      // List of fields that should be sent to the API
      const fieldsToSend = ['doctor_name', 'designation_name', 'qualifications', 'experience_years', 'languages', 'photo_url'];
      
      // Add all form fields (only include fields that have values)
      fieldsToSend.forEach((key) => {
        if (form[key] !== undefined && form[key] !== null && form[key] !== "") {
          // Convert experience_years to integer if it's a number
          if (key === 'experience_years') {
            const value = parseInt(form[key], 10);
            if (!isNaN(value)) {
              formData.append(key, value);
            } else {
              formData.append(key, form[key]);
            }
          } else {
            formData.append(key, form[key]);
          }
        }
      });
      
      // Add photo file if provided
      if (form.photo_file) {
        formData.append('photo_file', form.photo_file);
      }
      
      // Log FormData contents for debugging
      console.log('PATCH - FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? `[File: ${pair[1].name}]` : pair[1]));
      }
      
      await onPatchDoctorStatic(docId, formData, true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsEditable(false);
    setCurrentDoctorStatic({});
    setForm({
      doctor_name: "",
      photo_url: "",
      designation_name: "",
      qualifications: "",
      experience_years: "",
      languages: "",
      photo_file: null,
    });
    setPhotoPreview(null);
  };

  const onCloseModal = () => {
    handleClose();
  };

  return (
    <Modal
      showModal={isOpen}
      onClose={onCloseModal}
      modalHeight="h-[90%]"
      modalWidth="w-[80%]"
    >
      <div className="p-5 overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEditable ? t("Edit Doctor Information") : t("Add Doctor Information")}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
        {/* Image Section at Top */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t("Doctor Photo")}
          </label>
          <div className="flex flex-col items-center">
            {/* Photo Preview/Display */}
            {photoPreview ? (
              <div className="mb-4">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-full border-4 border-gray-200 shadow-md"
                />
              </div>
            ) : form.photo_url ? (
              <div className="mb-4">
                <img
                  src={form.photo_url}
                  alt="Doctor"
                  className="w-32 h-32 object-cover rounded-full border-4 border-gray-200 shadow-md"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 hidden">
                  <svg
                    className="w-16 h-16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              </div>
            ) : (
              <div className="mb-4 w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                <svg
                  className="w-16 h-16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            )}
            
            {/* Photo URL Input */}
            <div className="w-full mb-3">
              <input
                type="text"
                name="photo_url"
                value={form.photo_url}
                onChange={handleInputChange}
                placeholder="https://example.com/photo.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7] text-sm"
              />
            </div>
            
            {/* Photo File Input */}
            <div className="w-full">
              <input
                type="file"
                name="photo_file"
                accept="image/*"
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7] text-sm"
              />
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("Doctor Name")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="doctor_name"
            value={form.doctor_name}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("Designation")}
          </label>
          <input
            type="text"
            name="designation_name"
            value={form.designation_name}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("Qualifications")}
          </label>
          <input
            type="text"
            name="qualifications"
            value={form.qualifications}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("Experience (Years)")}
          </label>
          <input
            type="number"
            name="experience_years"
            value={form.experience_years}
            onChange={handleInputChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("Languages")}
          </label>
          <input
            type="text"
            name="languages"
            value={form.languages}
            onChange={handleInputChange}
            placeholder="e.g., English, Hindi, Telugu"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4C6AF7]"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <TertiaryButton
            buttonName={t("Cancel")}
            onClick={handleClose}
          />
          <PrimaryButton
            buttonName={
              isEditable
                ? (isDoctorStaticUpdating || isDoctorStaticPatching)
                  ? t("Updating...")
                  : t("Update")
                : (isDoctorStaticUpdating || isDoctorStaticPatching)
                ? t("Adding...")
                : t("Add")
            }
            onClick={handleSubmit}
            disabled={isDoctorStaticCreating || isDoctorStaticUpdating || isDoctorStaticPatching}
          />
        </div>
      </form>
      </div>
    </Modal>
  );
};

export default CreateDoctorStaticModal;

